import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, InputAdornment, Alert, Box, Typography } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
// components
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import { GoldBorderButton, GoldBorderLoadingButton } from '../../../components/nav-section/vertical/style';
import { useDispatch } from '../../../redux/store';
import { closeRegister } from '../../../redux/slices/authPages';
import useLocales from '../../../hooks/useLocales';
import conObj from '../../../utils/connection-assistant';
import { toPlayerId } from '../../../utils/formatString';
import RegisterSuccessDialog from './RegisterSuccessDialog';
import RegisterErrorDialog from './RegisterErrorDialog';
import { getRegisterErrorMessage, isOtpError } from './registerErrors';
import { IS_OTP_READY } from '../../../config';

// ----------------------------------------------------------------------

export default function RegisterForm({ verification = {}, setVerification }) {
  const { register } = useAuth();

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const [referralType, setReferralType] = useState(null);

  const [supportedBanks, setSupportedBanks] = useState([]);

  const [successDetails, setSuccessDetails] = useState(null);

  const [errorMessage, setErrorMessage] = useState(null);

  const fullName = verification?.fullName || '';

  const RegisterSchema = Yup.object().shape({
    memberBankBankId: Yup.string().required(translate('please_select_x', { x: translate('bank') })),
    memberBankAccountName: Yup.string()
      .required(translate('x_is_required', { x: translate('bank_acc_name') }))
      .test(
        'matches-full-name',
        translate('bank_acc_name_must_match'),
        (value) => (value || '').trim().toUpperCase() === fullName.trim().toUpperCase()
      ),
    memberBankAccountNumber: Yup.string().required(translate('x_is_required', { x: translate('bank_acc_number') })),
  });

  useEffect(() => {
    const _referralCode = localStorage.getItem('referralCode');
    const _referralType = localStorage.getItem('referralType');

    if (_referralCode) {
      setReferralType(_referralType || 'aff');
    }
  }, []);

  useEffect(() => {
    getSupportedBanks();
  }, []);

  const defaultValues = {
    memberBankBankId: '',
    memberBankAccountName: '',
    memberBankAccountNumber: '',
    referralCode: localStorage?.getItem('referralCode') || '',
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const values = watch();

  const getSupportedBanks = () => {
    conObj
      .post('transactions/supported_banks.json')
      .then((res) => {
        if (res.status === 200) {
          setSupportedBanks(res.data?.data?.data[0].Bank || []);
        }
      })
      .catch((err) => {
        console.error(`Error: ${err.message}`);
      });
  };

  const onSubmit = async (data) => {
    try {
      const username = toPlayerId(fullName);

      await register({
        username,
        fullName,
        password: verification?.password,
        profileNoHp: verification?.number,
        email: '',
        referralCode: data.referralCode,
        memberBankBankId: data.memberBankBankId,
        memberBankAccountName: data.memberBankAccountName.trim(),
        memberBankAccountNumber: data.memberBankAccountNumber.trim(),
        ...(IS_OTP_READY ? { otpCode: verification?.otpCode, otpUuid: verification?.id } : {}),
      });

      const referralCode = localStorage.getItem('referralCode');
      const selectedBank = supportedBanks?.find((f) => String(f.id) === String(data.memberBankBankId));

      setSuccessDetails({
        username: referralType === 'ag' ? `${referralCode}${username}` : username,
        password: verification?.password,
        bankName: selectedBank?.name,
        bankAccountName: data.memberBankAccountName.trim(),
        bankAccountNumber: data.memberBankAccountNumber.trim(),
      });
    } catch (error) {
      console.error(error);
      const message = getRegisterErrorMessage(error, translate);

      if (IS_OTP_READY && isOtpError(message) && setVerification) {
        setVerification((prev) => ({ ...prev, isVerified: false, otpCode: '', otpError: message }));
        return;
      }

      setError('afterSubmit', { ...error, message });
      setErrorMessage(message);
    }
  };

  const handleBack = () => {
    if (!setVerification) return;

    // With OTP on the previous screen is the code entry; otherwise it's the details step.
    setVerification((prev) =>
      IS_OTP_READY && prev?.id
        ? { ...prev, isVerified: false, otpCode: '', otpError: null }
        : { ...prev, detailsSubmitted: false }
    );
  };

  const handleConfirmSuccess = () => {
    reset();
    dispatch(closeRegister());
    
    window.location.reload();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

            {verification?.isVerified && <Alert severity="success">{translate('success_verification')}</Alert>}

            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ color: '#c9c9c9' }}>
                {translate('your_id')}
              </Typography>
              <Typography className="gold-text" variant="subtitle1" fontWeight={900}>
                {referralType === 'ag' ? `${values.referralCode || ''}${toPlayerId(fullName)}` : toPlayerId(fullName)}
              </Typography>
            </Stack>

            <RHFSelect name="memberBankBankId" label={translate('bank')} size="small" SelectProps={{ native: true }}>
              <option value="">{translate('please_select_a_bank')}</option>
              {supportedBanks?.map((item, index) => (
                <option key={index} value={item?.id}>
                  {item?.name}
                </option>
              ))}
            </RHFSelect>

            <RHFTextField name="memberBankAccountName" label={translate('bank_acc_name')} />

            <RHFTextField name="memberBankAccountNumber" label={translate('bank_acc_number')} />

            <Typography style={{ fontSize: '.75rem', color: '#ffcf5c', fontWeight: 500 }}>
              {translate('withdraw_name_mismatch_remark')}
            </Typography>

            <RHFTextField
              name="referralCode"
              disabled={!!localStorage?.getItem('referralCode')}
              label={translate('referral_code')}
              InputProps={{
                startAdornment:
                  referralType === 'ag' ? (
                    <InputAdornment position="start">
                      <Typography fontWeight={900}>{values.referralCode}</Typography>
                    </InputAdornment>
                  ) : (
                    <></>
                  ),
              }}
            />

            <Stack direction="row" spacing={1}>
              <GoldBorderButton
                fullWidth
                size="large"
                onClick={handleBack}
                disabled={isSubmitting}
                sx={{ color: 'white', flex: 1 }}
              >
                {translate('back')}
              </GoldBorderButton>

              <GoldBorderLoadingButton
                fullWidth
                variant="outlined"
                size="large"
                type="submit"
                loading={isSubmitting}
                sx={{ flex: 2 }}
              >
                {translate('register')}
              </GoldBorderLoadingButton>
            </Stack>
          </Stack>
        </FormProvider>
      </Box>

      <RegisterSuccessDialog open={!!successDetails} details={successDetails} onConfirm={handleConfirmSuccess} />

      <RegisterErrorDialog open={!!errorMessage} message={errorMessage} onClose={() => setErrorMessage(null)} />
    </Box>
  );
}

RegisterForm.propTypes = {
  verification: PropTypes.object,
  setVerification: PropTypes.func,
};
